import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { DsyInput } from '@dsy/enelx';
import { FilterEntry, FilterOptions, FilterValues } from '../../../models/filters.model';
import { HandoverLogFilterOptions, HandoverLogFilterValues } from '../../../models/handover-filter.model';
import { DueTagLabel } from '../../../models/handover-log.model';
import { SearchableDropdownComponent } from '../../../shared/components/searchable-dropdown/searchable-dropdown.component';

@Component({
  selector: 'dr-ops-stream-ui-handover-filters',
  standalone: true,
  imports: [SearchableDropdownComponent],
  templateUrl: './handover-filters.component.html',
  styleUrls: ['./handover-filters.component.scss', '../../../shared/styles/filters.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})

export class HandoverFiltersComponent {
  @ViewChild('dueDateInput', { read: ElementRef }) dueDateInput!: ElementRef<DsyInput>;
  readonly options = input.required<HandoverLogFilterOptions>();
  readonly filtersChange = output<HandoverLogFilterValues>();

  readonly activitySignal = signal<string | null>(null);
  readonly programSignal = signal<string | null>(null);
  readonly tagSignal = signal<string | null>(null);
  readonly createdBySignal = signal<string | null>(null);
  readonly dueDateSignal = signal<Date | null>(null);

  private hasAppliedFilters: boolean = false;

  readonly currentFiltersSignal = computed<HandoverLogFilterValues>(() => ({
    activity: this.activitySignal(),
    program: this.programSignal(),
    createdBy: this.createdBySignal(),
    dueDate: this.dueDateSignal(),
    status: null,
    tag: this.tagSignal(),
  }));

  onActivityChange = (value: string): void => this.activitySignal.set(value);
  onProgramChange = (value: string): void => this.programSignal.set(value);
  onCreatedByChange = (value: string): void => this.createdBySignal.set(value);
  onDueDateChange = (value: string): void => this.dueDateSignal.set(value ? new Date(value) : null);
  onTagChange = (value: string): void => this.tagSignal.set(value);

  readonly applyButtonDisabledSignal = computed(
    () =>
      !this.currentFiltersSignal().activity &&
      !this.currentFiltersSignal().program &&
      !this.currentFiltersSignal().createdBy &&
      !this.currentFiltersSignal().dueDate &&
      !this.currentFiltersSignal().tag
  );

  // filter entries for activity, program, and createdBy
  readonly activityFilterEntriesSignal = computed<FilterEntry[]>(() =>
    this.createFilterEntries(this.options().activities, this.activitySignal(), (v) => this.activitySignal.set(v))
  );
  readonly programFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().programs, this.programSignal(), (v) => this.programSignal.set(v)));
  readonly createdByFilterEntriesSignal = computed<FilterEntry[]>(() =>
    this.createFilterEntries(this.options().creators, this.createdBySignal(), (v) => this.createdBySignal.set(v))
  );
  readonly tagFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().tags, this.tagSignal(), (v) => this.tagSignal.set(v)));

  private createFilterEntries(labels: string[], currentValue: string | null, setter: (value: string | DueTagLabel) => void): FilterEntry[] {
    return labels.map((label, i) => ({
      id: i + 1,
      labelContent: label,
      isSelected: currentValue === label,
    }));
  }

  onReset(): void {
    this.activitySignal.set(null);
    this.programSignal.set(null);
    this.createdBySignal.set(null);
    this.dueDateSignal.set(null);
    this.tagSignal.set(null);
    this.clearDate();
    if (this.hasAppliedFilters) {
      this.filtersChange.emit(this.currentFiltersSignal());
      this.hasAppliedFilters = false;
    }
  }

  private clearDate(): void {
    if (!this.dueDateInput) return;

    const inputField = this.dueDateInput.nativeElement.inputField?.value;
    if (inputField instanceof HTMLInputElement && inputField.value) {
      inputField.value = '';
    }
  }

  onApply(): void {
    this.filtersChange.emit(this.currentFiltersSignal());
    this.hasAppliedFilters = true;
  }
}
